import { useCallback, useEffect } from 'react'
import { Keypair } from '@solana/web3.js'
import { useRouter } from 'next/router'
import { LAST_WALLET_NAME, SLIPPAGE_KEY, LANGUAGE_KEY } from 'utils/constants'
import { useWallet } from '@solana/wallet-adapter-react'
import { useLocalStorage } from 'hooks/useLocalStorageState'
import store from 'stores/store'
import { useManifestMarket } from 'hooks/useManifestMarket'
import { milliseconds } from 'date-fns'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'next-i18next'
import usePrevious from 'hooks/usePrevious'
import type { BuildIdResponse } from '../pages/api/build-id'

const set = store.getState().set

const HydrateStore = () => {
  const router = useRouter()
  const connection = store((s) => s.connection)
  // const slowNetwork = useNetworkSpeed()
  const slippage = store((s) => s.swap.slippage)
  const { wallet } = useWallet()
  const baseMint = store((s) => s.market.baseToken.mint)
  const quoteMint = store((s) => s.market.quoteToken.mint)
  const { asPath } = useRouter()
  const [, setLastWalletName] = useLocalStorage(LAST_WALLET_NAME)
  const [storeSlippage] = useLocalStorage(SLIPPAGE_KEY)
  const [savedLanguage, setSavedLanguage] = useLocalStorage(LANGUAGE_KEY)
  const { i18n } = useTranslation()

  const previousSavedLanguage = usePrevious(savedLanguage)

  useEffect(() => {
    if (!i18n.language || !i18n.changeLanguage) {
      return
    }

    function applyNewLocale(locale: string) {
      void i18n.changeLanguage(locale)
      void router.push(
        { pathname: router.pathname, query: router.query },
        router.asPath,
        { locale },
      )
    }

    const localeCandidate = window.location.pathname.split('/')[1]
    const currentPathLocale =
      localeCandidate &&
      router.locales?.find((locale) => locale === localeCandidate)

    if (
      previousSavedLanguage == null && // if it's the first render
      currentPathLocale &&
      currentPathLocale !== savedLanguage
    ) {
      // 1. it's the first render
      // 2. if the locale in the URL is valid
      // 3. if locale is different from saved language
      // => the user must have entered a url with a locale part
      // we want to apply such a locale because it is intentional user action to change the locale via URL
      setSavedLanguage(currentPathLocale)
      applyNewLocale(currentPathLocale)
    } else if (
      previousSavedLanguage !== savedLanguage &&
      router.locale !== savedLanguage
    ) {
      // 1. if update from url didn't run
      // 2. if the locale in the router is different from the saved language
      // 3. if language just changed
      // => we want to update it
      applyNewLocale(savedLanguage)
    }
  }, [
    i18n,
    i18n.changeLanguage,
    i18n.language,
    previousSavedLanguage,
    router,
    savedLanguage,
    setSavedLanguage,
  ])

  useEffect(() => {
    try {
      if (slippage.toString() !== storeSlippage) {
        set((s) => {
          s.swap.slippage = parseFloat(storeSlippage)
        })
      }
    } catch (e) {
      console.error('Error setting slippage', e)
    }
  }, [slippage, storeSlippage])

  useEffect(() => {
    // Route loading state handlers
    const handleRouteChangeStart = () =>
      set((state) => {
        state.routeLoading = true
      })

    const handleRouteChangeComplete = () => {
      set((state) => {
        state.routeLoading = false
      })

      // Scroll to the top after route change completes
      if (typeof window !== 'undefined') {
        window.scrollTo(0, 0)
      }
    }

    // Attach event listeners
    router.events.on('routeChangeStart', handleRouteChangeStart)
    router.events.on('routeChangeComplete', handleRouteChangeComplete)
    router.events.on('routeChangeError', handleRouteChangeComplete)

    // Cleanup on unmount
    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart)
      router.events.off('routeChangeComplete', handleRouteChangeComplete)
      router.events.off('routeChangeError', handleRouteChangeComplete)
    }
  }, [router.events])

  const handleWindowResize = useCallback(() => {
    if (typeof window !== 'undefined') {
      set((s) => {
        s.window.width = window.innerWidth
        s.window.height = window.innerHeight
      })
    }
  }, [])
  // store the window width and height on resize
  useEffect(() => {
    handleWindowResize()
    window.addEventListener('resize', handleWindowResize)
    return () => window.removeEventListener('resize', handleWindowResize)
  }, [handleWindowResize])

  useEffect(() => {
    if (wallet?.adapter) {
      setLastWalletName(wallet.adapter.name)
    }
  }, [wallet, setLastWalletName])

  // The websocket library solana/web3.js closes its websocket connection when the subscription list
  // is empty after opening it for the first time, preventing subsequent subscriptions from receiving responses.
  // This is a hack to prevent the list from ever getting empty
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    const id = connection.onAccountChange(new Keypair().publicKey, () => {})
    return () => {
      void connection.removeAccountChangeListener(id)
    }
  }, [connection])

  const { manifestMarket, initialLoadingManifestMarket } = useManifestMarket(
    baseMint,
    quoteMint,
    asPath === '/' ||
      asPath.startsWith('/token/') ||
      asPath.startsWith('/swap'),
  )

  // set manifest market
  useEffect(() => {
    set((state) => {
      state.market.loadingManifestMarket = initialLoadingManifestMarket
      state.market.manifestMarket = manifestMarket ?? undefined
    })
  }, [manifestMarket, initialLoadingManifestMarket])

  const newBuildAvailable = store((s) => s.newBuildAvailable)
  const buildId = useBuildId(newBuildAvailable)
  useEffect(() => {
    if (!buildId || !process.env.BUILD_ID || buildId === process.env.BUILD_ID) {
      return
    }

    // there's a new version deployed that we need to load
    set((state) => {
      state.newBuildAvailable = true
    })
  }, [buildId])

  return null
}

const Provider = () => {
  return (
    <>
      <HydrateStore />
    </>
  )
}

export default Provider

function useBuildId(isNewBuildAvailable: boolean) {
  const { data: buildId } = useQuery({
    queryKey: ['build-id'],
    queryFn: async () => {
      try {
        const response = await fetch('/api/build-id')
        const { buildId } = (await response.json()) as BuildIdResponse
        if (!buildId) {
          console.error('Build ID not found')
          return null
        }

        return buildId
      } catch (error) {
        console.error('Error fetching build ID:', error)
        return null
      }
    },
    staleTime: milliseconds({ minutes: 10 }),
    cacheTime: milliseconds({ minutes: 10 }),
    refetchInterval: milliseconds({ minutes: 5 }),
    refetchOnWindowFocus: true,
    enabled: !isNewBuildAvailable,
  })

  return buildId
}
